import React, { useState, useEffect } from 'react';
import { analyzeSignificantFigures, SignificantFigureResult } from './utils/significantFigures';
import { ErrorChart } from './components/ErrorChart';
import { SignificantFigureConverter } from './components/SignificantFigureConverter';

function App() {
  const [input, setInput] = useState<string>('');
  const [result, setResult] = useState<SignificantFigureResult | null>(null);
  const [examples] = useState([
    '123.456',
    '0.00123',
    '1.23e-3',
    '1000',
    '1000.0',
    '0.100',
    '1.23456e5'
  ]);

  useEffect(() => {
    const analysis = analyzeSignificantFigures(input);
    setResult(analysis);
  }, [input]);

  const handleExampleClick = (example: string) => {
    setInput(example);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            リアルタイム有効数字チェッカー
          </h1>
          <p className="text-lg text-gray-600">
            物理・化学計算向けの有効数字解析ツール
          </p>
        </div>

        {/* 入力セクション */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">数値入力</h2>
          <div className="space-y-4">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="数値を入力してください（例: 123.456, 1.23e-3）"
              className="input-field"
            />
            
            {/* 例文ボタン */}
            <div>
              <p className="text-sm text-gray-600 mb-2">例:</p>
              <div className="flex flex-wrap gap-2">
                {examples.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(example)}
                    className="btn-secondary text-sm"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 結果表示 */}
        {result && (
          <div className="space-y-6">
            {/* 基本情報 */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">基本解析</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{result.significantDigits}</div>
                  <div className="text-sm text-blue-700">有効数字の数</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-lg font-mono text-green-600">{result.significantFigures}</div>
                  <div className="text-sm text-green-700">有効数字表示</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-lg font-mono text-purple-600">{result.scientificNotation}</div>
                  <div className="text-sm text-purple-700">科学表記</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-lg font-mono text-orange-600">{result.originalNumber}</div>
                  <div className="text-sm text-orange-700">元の数値</div>
                </div>
              </div>
            </div>

            {/* 丸め処理 */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">丸め処理シミュレーション</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-lg font-mono text-yellow-600">{result.roundedResults.round}</div>
                  <div className="text-sm text-yellow-700">四捨五入</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-lg font-mono text-red-600">{result.roundedResults.ceil}</div>
                  <div className="text-sm text-red-700">切り上げ</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-lg font-mono text-blue-600">{result.roundedResults.floor}</div>
                  <div className="text-sm text-blue-700">切り捨て</div>
                </div>
              </div>
            </div>

            {/* 誤差解析グラフ */}
            <ErrorChart
              relativeError={result.errorAnalysis.relativeError}
              absoluteError={result.errorAnalysis.absoluteError}
              significantDigits={result.significantDigits}
            />

                    {/* 物理・化学計算ガイド */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">物理・化学計算での有効桁処理</h3>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">加減算のルール</h4>
              <p className="text-sm text-gray-600">
                小数点以下の桁数が最も少ないものに合わせる。例: 12.34 + 5.6 = 17.9
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">乗除算のルール</h4>
              <p className="text-sm text-gray-600">
                有効数字が最も少ないものに合わせる。例: 12.34 × 5.6 = 69
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">現在の数値の精度</h4>
              <p className="text-sm text-gray-600">
                有効数字: {result.significantDigits}桁 | 
                相対誤差: {result.errorAnalysis.relativeError.toFixed(4)}% | 
                絶対誤差: {result.errorAnalysis.absoluteError.toExponential(2)}
              </p>
            </div>
          </div>
        </div>

              </div>
    )}

    {/* 有効桁数変換ツール */}
    <SignificantFigureConverter />

        {/* 使用方法 */}
        {!result && input && (
          <div className="card">
            <div className="text-center text-gray-500">
              <p>有効な数値を入力してください</p>
              <p className="text-sm mt-2">例: 123.456, 0.00123, 1.23e-3</p>
            </div>
          </div>
        )}

        {/* フッター */}
        <div className="text-center mt-12 text-gray-500">
          <p className="text-sm">
            物理・化学の学生・高専生向け有効数字解析ツール
          </p>
          <p className="text-xs mt-2">
            Made by <span className="font-semibold">tikisan</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default App; 